import type { ChartPointsRequest, ChartRange } from "@/api/datasets";
import { asRecord } from "@/components/shapes/shared/plain-record";
import { formatNumber } from "@/i18n";

export interface ChartPoint {
  label: string;
  value: number;
}

/** The valid `{label, value}` points of chart content; anything else is dropped. */
export function chartPointsFrom(value: unknown): ChartPoint[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((point: unknown) => {
    if (!point || typeof point !== "object") return [];
    const p = point as Record<string, unknown>;
    return typeof p.value === "number" && Number.isFinite(p.value)
      ? [{ label: typeof p.label === "string" ? p.label : "", value: p.value }]
      : [];
  });
}

export function formatChartValue(value: number): string {
  return formatNumber(value, { maximumFractionDigits: 1 });
}

/** Point `index`'s change on the one before, as a signed percentage. */
export function chartChangeText(
  points: readonly ChartPoint[],
  index: number,
): string | null {
  const value = points[index]?.value;
  const before = points[index - 1]?.value;
  if (value === undefined || before === undefined || before === 0) return null;
  const change = Math.round(((value - before) / Math.abs(before)) * 100);
  return `${change > 0 ? "+" : ""}${change}%`;
}

/** How a chart draws its points. */
export type ChartKind = "bar" | "line" | "number";

/** A chart's `kind`; absent or unknown (every older chart) draws bars. */
export function chartKindFrom(value: unknown): ChartKind {
  return value === "line" || value === "number" ? value : "bar";
}

/** A chart bound to a Data frame: the table and how its points are computed. */
export interface ChartSource {
  /** The Data frame's id. */
  table: string;
  /** The source's fields and aggregate, without its range. */
  request: Omit<ChartPointsRequest, "range">;
  /** The period the stored points cover. */
  range: ChartRange;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

function rangeFrom(value: unknown): ChartRange {
  if (value === "7d" || value === "30d" || value === "90d") return value;
  const span = asRecord(value);
  return span &&
    typeof span.from === "string" &&
    typeof span.to === "string" &&
    DAY.test(span.from) &&
    DAY.test(span.to)
    ? { from: span.from, to: span.to }
    : "all";
}

/** A chart content's `source`, or null when it reads no Data frame. */
export function chartSourceFrom(value: unknown): ChartSource | null {
  const source = asRecord(value);
  if (
    !source ||
    typeof source.table !== "string" ||
    typeof source.label_field !== "string" ||
    typeof source.aggregate !== "string"
  ) {
    return null;
  }
  return {
    table: source.table,
    request: {
      label_field: source.label_field,
      value_field:
        typeof source.value_field === "string" ? source.value_field : null,
      aggregate: source.aggregate,
      ...(typeof source.limit === "number" ? { limit: source.limit } : {}),
      ...(typeof source.date_field === "string"
        ? { date_field: source.date_field }
        : {}),
    },
    range: rangeFrom(source.range),
  };
}

/** What a chart drawing (bars, line) takes from `ChartFace`. */
export interface ChartDrawingProps {
  title: string;
  unit: string;
  points: readonly ChartPoint[];
  /** The index of the point the value shows. */
  shown: number;
  /** The index a person picked, or null for the latest. */
  selected: number | null;
  onSelect: (index: number | null) => void;
  opened: boolean;
  testIdPrefix: string;
}
